import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { getTranslate, getActiveLanguage } from 'react-localize-redux';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import { push } from 'react-router-redux';
import routes from 'library/Routes';
import LayoutHelper from 'infrastructure/LayoutHelper';
import UserHelper from 'infrastructure/UserHelper';
import {
  Grid,
  Snackbar,
  SnackbarContent,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Avatar,
  Collapse,
  Link,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import queryString from 'query-string';
import { ClientRatePlanForm } from './components/ClientRatePlanForm/ClientRatePlanForm';
import { calculatePriceAfterDiscount } from '../../../utils/price';
import { formatDiscount, formatPrice } from '../../../utils/format';
import moment from 'moment';
import { withSnackbar } from 'notistack';
import {
  BookingRatePlanViewProps,
  BookingRatePlanViewState,
  Discount,
  OnlinePayment,
  Owner,
  RatePlan,
  Studio,
  Teacher,
} from './BookingRatePlanView.types';
import styles from './BookingRatePlanView.styles';
import TelegramWebViewDetector from './components/TelegramWebViewDetector/TelegramWebViewDetector';
import OpenBrowserInstructionGif from './OpenBrowserInstruction.gif';

// Named export (in addition to the connected/styled/snackbar-wrapped default) so unit tests can
// instantiate the raw class directly - e.g. to exercise getPrice() without mounting the full tree.
export class BookingRatePlanView extends React.Component<BookingRatePlanViewProps, BookingRatePlanViewState> {
  constructor(props: BookingRatePlanViewProps) {
    super(props);

    const { user, domain, match } = props;
    let clientToAdd;
    let isGuest = true;

    if (UserHelper.isLoggedIn(user)) {
      clientToAdd = user;
      isGuest = false;
    } else {
      clientToAdd = {
        phone: undefined,
        email: undefined,
        name: undefined,
        domain: domain.id,
      };
    }

    const pageState = window.__PAGE_STATE__;

    this.state = {
      clientToAdd,
      studio: pageState?.studio,
      owner: pageState?.studioOwner,
      isGuest,
      canSubmit: !isGuest,
      course: pageState?.course,
      groupId: match.params.groupId,
      attending: 0,
      teachersMap: {},
      teachers: [],
      isNewClient: true,
      showErrorSnackbar: false,
    };
  }

  componentDidMount() {
    const { match } = this.props;
    const { ownerId, ratePlanId } = match.params;

    this.loadStudioClient(ownerId);

    if (!this.state.owner) {
      this.loadOwner(ownerId);
    }
    this.loadOrder(ownerId);
    this.loadRatePlan(ownerId, ratePlanId);
  }

  render(): JSX.Element {
    const { classes, translate } = this.props;
    const { loadingOnlinePayment } = this.state;

    return (
      <div className={classes.root} style={{ paddingTop: '2rem' }}>
        <div style={{ display: 'none' }}>
          <p>Посилання на GIF: {JSON.stringify(OpenBrowserInstructionGif)}</p>
          <img
            src={OpenBrowserInstructionGif}
            alt="Debug GIF"
            onError={(e) => console.error('GIF failed to load in debug element')}
          />
        </div>

        <TelegramWebViewDetector translate={translate} instructionGifUrl={OpenBrowserInstructionGif} />

        <div>
          {loadingOnlinePayment ? (
            LayoutHelper.renderLoader()
          ) : (
            <Fragment>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  {this.renderRatePlan()}
                </Grid>
                <Grid item xs={12}>
                  {this.renderForm()}
                </Grid>
              </Grid>

              {this.renderErrorSnackbar()}
            </Fragment>
          )}
        </div>
      </div>
    );
  }

  loadStudioClient = async (ownerId: string): Promise<void> => {
    const { studioClientId } = this.props;

    if (studioClientId) {
      this.setState({ loadingClient: true });

      try {
        const api = serviceLocator.clientApi;
        const clientData = await api.getMyClient(ownerId, studioClientId);
        const client = clientData.client;
        this.setState({ studioClient: client, loadingClient: false });
        serviceLocator.promocodeApiCall
          .active(ownerId, client._id)
          .then(({ application }) => this.setState({ promocode: application }))
          .catch(() => this.setState({ promocode: null }));
      } catch (error) {
        this.setState({ loadingClient: false });
        console.error('Error loading studio client:', error);
      }
    }
  };

  onSuccess = (ownerId: string): void => {
    this.loadOwner(ownerId);
    this.loadOrder(ownerId);
  };

  loadOrder = async (ownerId: string): Promise<void> => {
    const { orderId } = this.props;

    if (orderId) {
      this.setState({ loadingOnlinePayment: true });
      const api = serviceLocator.paymentApi;

      let timerId = setInterval(async () => {
        try {
          const onlinePayment: OnlinePayment = await api.getOnlinePaymentById(ownerId, orderId as string);

          if (onlinePayment) {
            if (onlinePayment.orderData.clientRatePlanId || onlinePayment.status === 'SUCCESS') {
              clearInterval(timerId as any);
              this.props.studioClient(onlinePayment.ownerId, onlinePayment.studioClientId, orderId as string);
              return;
            }
          }
        } catch (error) {
          console.error('Error polling order status:', error);
        }
      }, 3000);

      setTimeout(() => {
        clearInterval(timerId as any);
        this.setState({ loadingOnlinePayment: false });
      }, 12000);
    }
  };

  loadOwner = (ownerId: string): void => {
    const api = serviceLocator.userApi;
    api
      .getOwnerInfo(ownerId)
      .then((owner: Owner) => {
        this.setState({ owner });
      })
      .catch(LayoutHelper.onErr);
  };

  loadTeachers = (ownerId: string): void => {
    const api = serviceLocator.teacherApi;

    api
      .getPublicTeachers(ownerId)
      .then((data: { teachers: Teacher[] }) => {
        const teachers = data.teachers;
        let teachersMap: Record<string, Teacher> = {};

        for (let i = 0; i < teachers.length; i++) {
          const teacher = teachers[i];
          teachersMap[teacher._id] = teacher;
        }

        this.setState({ teachers, teachersMap });
      })
      .catch(LayoutHelper.onErr);
  };

  loadRatePlan = (ownerId: string, ratePlanId: string): void => {
    this.setState({ loadingRatePlan: true });
    const api = serviceLocator.ratePlanApiCall;

    api
      .getRatePlan(ownerId, ratePlanId)
      .then((data: RatePlan) => {
        this.setState({ ratePlan: data, loadingRatePlan: false });
      })
      .catch(LayoutHelper.onErrSnack(this.props.enqueueSnackbar));
  };

  findDiscount = (ratePlanId: string): Discount | undefined => {
    const { studioClient } = this.state;

    if (!studioClient?.discounts) {
      return undefined;
    }

    return studioClient.discounts.find((item: Discount) => item.ratePlanId === ratePlanId);
  };

  getPrice = (): number => {
    const { promocode, ratePlan } = this.state;

    if (!ratePlan) {
      return 0;
    }

    const discount = this.findDiscount(ratePlan._id);
    const personalPrice = discount
      ? calculatePriceAfterDiscount(ratePlan.price, discount.discount, discount.discountType)
      : ratePlan.price;
    const promoPrice = promocode
      ? calculatePriceAfterDiscount(ratePlan.price, promocode.promocode.discountValue, promocode.promocode.discountType)
      : ratePlan.price;
    const price = Math.min(personalPrice, promoPrice);
    // Floor to the smallest unit whenever the discount (personal or promocode) brings the price
    // to zero or below, not just for promocodes - matches the actual checkout amount.
    return price <= 0 ? 1 : price;
  };

  handleCloseErrorSnackbar = () => {
    this.setState({ showErrorSnackbar: false });
  };

  renderErrorSnackbar = (): JSX.Element => {
    const { translate } = this.props;
    const { showErrorSnackbar } = this.state;

    return (
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={showErrorSnackbar || false}
        onClose={this.handleCloseErrorSnackbar}
      >
        <SnackbarContent
          message={translate('manageRegistration.bookingError')}
          action={[
            <IconButton key="close" aria-label="Close" color="inherit" onClick={this.handleCloseErrorSnackbar}>
              <CloseIcon />
            </IconButton>,
          ]}
          style={{ backgroundColor: '#d32f2f' }}
        />
      </Snackbar>
    );
  };

  renderRatePlan = (): JSX.Element => {
    if (this.state.loadingRatePlan || !this.state.ratePlan || !this.state.owner) {
      return LayoutHelper.renderLoader();
    }

    const { translate, domain, classes } = this.props;
    const { owner, ratePlan } = this.state;

    const termsAndConditions = UserHelper.getTermsAndConditions(owner);
    const currencyCode = domain.currencyCode;
    ratePlan.discount = this.findDiscount(ratePlan._id);
    const price = this.getPrice();

    return (
      <Card>
        <CardHeader
          action={
            <IconButton
              onClick={() => {
                this.setState({
                  showPricingDetails: !this.state.showPricingDetails,
                });
              }}
            >
              <InfoIcon />
            </IconButton>
          }
          avatar={<Avatar src={owner.avatar} className={classes.avatar}></Avatar>}
          title={ratePlan.name}
          subheader={<Fragment>{formatPrice(price, currencyCode)}</Fragment>}
        ></CardHeader>
        <Collapse in={this.state.showPricingDetails} timeout="auto">
          <CardContent>
            {ratePlan.discount && ratePlan.discount.discount > 0 && (
              <Typography variant="caption" color="textPrimary">
                ({translate('myClients.withDiscount')}: {formatDiscount(ratePlan.discount, currencyCode)})
              </Typography>
            )}
            <Typography>{ratePlan.description}</Typography>
          </CardContent>
          <CardContent>
            {termsAndConditions && (
              <Typography variant="caption">
                <Link href={termsAndConditions} target="_blank">
                  {translate('manageRegistration.termsAndConditions')}
                  &nbsp;{owner.name}
                </Link>
              </Typography>
            )}
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  renderForm = (): JSX.Element | React.ReactNode => {
    const { owner, promocode, ratePlan, loadingClient, studioClient } = this.state;
    const { date, paymentSource } = this.props;

    if (!owner || !ratePlan) {
      return LayoutHelper.renderLoader();
    }

    if (loadingClient) {
      return LayoutHelper.renderLoader();
    }

    const formDate = date ? moment.utc(date, 'DD-MM-YYYY').startOf('day') : moment.utc().startOf('day');

    return (
      <ClientRatePlanForm
        studioClient={studioClient}
        owner={owner}
        ratePlan={ratePlan}
        promocode={promocode}
        onPromocodeApplied={(application) => this.setState({ promocode: application })}
        date={formDate}
        paymentSource={paymentSource}
      />
    );
  };
}

interface StateProps {
  locale: any;
  user: any;
  studio: Studio;
  domain: {
    domain: {
      id: string;
      currencyCode: string;
    };
  };
}

const mapStateToProps = (state: StateProps, ownProps: any) => {
  const queryParams = queryString.parse(ownProps.location.search);

  return {
    translate: getTranslate(state.locale),
    currentLanguage: getActiveLanguage(state.locale).code,
    user: state.user,
    studio: state.studio,
    domain: state.domain.domain,
    date: queryParams.date as string | undefined,
    orderId: queryParams.orderId as string | undefined,
    studioClientId: queryParams.studioClientId as string | undefined,
    paymentSource: queryParams.paymentSource as string | undefined,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    registerToClass: (studio: Studio, classId: string) => {
      if (studio.domainName) {
        dispatch(push(`${routes.class()}?keyword=${''}&location=${''}`));
      } else {
        dispatch(push(`${routes.classId()}?keyword=${''}`));
      }
    },
    toClassSignUp: (link: string) => {
      dispatch(push(link));
    },
    studioClient: (ownerId: string, studioClientId: string, orderId: string) => {
      dispatch(
        push(
          `${routes.studioClientClasses({
            ownerId: ownerId,
            studioClientId: studioClientId,
          })}?orderId=${orderId}`
        )
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(withSnackbar(BookingRatePlanView)));
